export interface SessionRefreshUpdate {
  accessToken: string;
  refreshToken?: string;
  expiresAt: number;
}

interface CoordinatedSessionRefreshOptions<T extends SessionRefreshUpdate> {
  forceRefresh?: boolean;
  shouldRefresh: () => boolean;
  refresh: () => Promise<T>;
  apply: (update: SessionRefreshUpdate) => void;
}

interface SessionRefreshBroadcastMessage extends SessionRefreshUpdate {
  type: 'crm-session-refreshed';
}

const SESSION_REFRESH_CHANNEL_NAME = 'crm-session-refresh';
const SESSION_REFRESH_LOCK_NAME = 'crm-session-refresh';
const SESSION_REFRESH_STORAGE_LOCK_KEY = 'crm-session-refresh-lock';
const SESSION_REFRESH_STORAGE_LOCK_TTL_MS = 10_000;
const SESSION_REFRESH_STORAGE_LOCK_RETRY_MS = 50;
const SESSION_REFRESH_STORAGE_LOCK_MAX_WAIT_MS = 5_000;
const STORAGE_LOCK_OWNER_ID_RANDOM_BYTES = 16;
const HEX_RADIX = 16;
const HEX_BYTE_PAD_LENGTH = 2;

let sharedRefreshPromise: Promise<SessionRefreshUpdate | null> | null = null;
let sharedBroadcastChannel: BroadcastChannel | null = null;
const storageLockOwnerId = createStorageLockOwnerId();

interface SessionRefreshStorageLock {
  ownerId: string;
  expiresAt: number;
}

function createStorageLockOwnerId(): string {
  if (
    typeof window !== 'undefined' &&
    window.crypto &&
    typeof window.crypto.randomUUID === 'function'
  ) {
    return window.crypto.randomUUID();
  }

  if (
    typeof window !== 'undefined' &&
    window.crypto &&
    typeof window.crypto.getRandomValues === 'function'
  ) {
    const bytes = new Uint8Array(STORAGE_LOCK_OWNER_ID_RANDOM_BYTES);
    window.crypto.getRandomValues(bytes);
    const randomId = Array.from(bytes, (byte) =>
      byte.toString(HEX_RADIX).padStart(HEX_BYTE_PAD_LENGTH, '0')
    ).join('');
    return `session-refresh-${randomId}`;
  }

  return `session-refresh-${Date.now()}`;
}

function hasBroadcastChannel(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.BroadcastChannel === 'function'
  );
}

function getBroadcastChannel(): BroadcastChannel | null {
  if (!hasBroadcastChannel()) {
    return null;
  }

  if (!sharedBroadcastChannel) {
    sharedBroadcastChannel = new window.BroadcastChannel(
      SESSION_REFRESH_CHANNEL_NAME
    );
  }

  return sharedBroadcastChannel;
}

function isSessionRefreshBroadcastMessage(
  value: unknown
): value is SessionRefreshBroadcastMessage {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<SessionRefreshBroadcastMessage>;
  return (
    candidate.type === 'crm-session-refreshed' &&
    typeof candidate.accessToken === 'string' &&
    typeof candidate.expiresAt === 'number' &&
    Number.isFinite(candidate.expiresAt)
  );
}

function publishSessionRefresh(update: SessionRefreshUpdate): void {
  const channel = getBroadcastChannel();
  if (!channel) {
    return;
  }

  channel.postMessage({
    type: 'crm-session-refreshed',
    accessToken: update.accessToken,
    expiresAt: update.expiresAt,
  } satisfies SessionRefreshBroadcastMessage);
}

function getLocalStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const { localStorage } = window;
    const probeKey = `${SESSION_REFRESH_STORAGE_LOCK_KEY}:probe`;
    localStorage.setItem(probeKey, '1');
    localStorage.removeItem(probeKey);
    return localStorage;
  } catch {
    return null;
  }
}

function readStorageLock(storage: Storage): SessionRefreshStorageLock | null {
  const rawLock = storage.getItem(SESSION_REFRESH_STORAGE_LOCK_KEY);
  if (!rawLock) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawLock) as Partial<SessionRefreshStorageLock>;
    if (
      typeof parsed.ownerId === 'string' &&
      typeof parsed.expiresAt === 'number' &&
      Number.isFinite(parsed.expiresAt)
    ) {
      return {
        ownerId: parsed.ownerId,
        expiresAt: parsed.expiresAt,
      };
    }
  } catch {
    storage.removeItem(SESSION_REFRESH_STORAGE_LOCK_KEY);
  }

  return null;
}

function writeStorageLock(storage: Storage, ownerId: string): void {
  storage.setItem(
    SESSION_REFRESH_STORAGE_LOCK_KEY,
    JSON.stringify({
      ownerId,
      expiresAt: Date.now() + SESSION_REFRESH_STORAGE_LOCK_TTL_MS,
    } satisfies SessionRefreshStorageLock)
  );
}

function clearStorageLock(storage: Storage, ownerId: string): void {
  const activeLock = readStorageLock(storage);
  if (activeLock?.ownerId === ownerId) {
    storage.removeItem(SESSION_REFRESH_STORAGE_LOCK_KEY);
  }
}

function waitForStorageLockRetry(): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, SESSION_REFRESH_STORAGE_LOCK_RETRY_MS);
  });
}

async function runWithStorageRefreshLock<T>(
  callback: () => Promise<T>
): Promise<T> {
  const storage = getLocalStorage();
  if (!storage) {
    return callback();
  }

  const waitUntil = Date.now() + SESSION_REFRESH_STORAGE_LOCK_MAX_WAIT_MS;

  while (Date.now() <= waitUntil) {
    const activeLock = readStorageLock(storage);
    const now = Date.now();
    if (
      !activeLock ||
      activeLock.ownerId === storageLockOwnerId ||
      activeLock.expiresAt <= now
    ) {
      writeStorageLock(storage, storageLockOwnerId);
      const confirmedLock = readStorageLock(storage);
      if (confirmedLock?.ownerId === storageLockOwnerId) {
        try {
          return await callback();
        } finally {
          clearStorageLock(storage, storageLockOwnerId);
        }
      }
    }

    await waitForStorageLockRetry();
  }

  // The lock key contains no credentials. If it is stale or malformed, fail
  // open so the current tab can still recover the user's session.
  return callback();
}

async function runWithRefreshLock<T>(callback: () => Promise<T>): Promise<T> {
  if (
    typeof navigator !== 'undefined' &&
    navigator.locks &&
    typeof navigator.locks.request === 'function'
  ) {
    return navigator.locks.request(SESSION_REFRESH_LOCK_NAME, callback);
  }

  return runWithStorageRefreshLock(callback);
}

export function subscribeToSessionRefreshes(
  apply: (update: SessionRefreshUpdate) => void
): () => void {
  const channel = getBroadcastChannel();
  if (!channel) {
    return () => {};
  }

  function handleMessage(event: MessageEvent<unknown>): void {
    if (!isSessionRefreshBroadcastMessage(event.data)) {
      return;
    }

    apply({
      accessToken: event.data.accessToken,
      expiresAt: event.data.expiresAt,
    });
  }

  channel.addEventListener('message', handleMessage);
  return () => {
    channel.removeEventListener('message', handleMessage);
  };
}

export async function runCoordinatedSessionRefresh<
  T extends SessionRefreshUpdate,
>(options: CoordinatedSessionRefreshOptions<T>): Promise<T | null> {
  const forceRefresh = options.forceRefresh === true;
  if (sharedRefreshPromise) {
    if (forceRefresh) {
      await sharedRefreshPromise.catch(() => null);
    } else {
      return (await sharedRefreshPromise) as T | null;
    }
  }

  if (sharedRefreshPromise) {
    return (await sharedRefreshPromise) as T | null;
  }

  sharedRefreshPromise = runWithRefreshLock(async () => {
    if (!forceRefresh && !options.shouldRefresh()) {
      return null;
    }

    const update = await options.refresh();
    options.apply(update);
    publishSessionRefresh(update);
    return update;
  }).finally(() => {
    sharedRefreshPromise = null;
  });

  return (await sharedRefreshPromise) as T | null;
}
